import { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';

import PlanContainer from '../../../../components/PlanContainer';
import CtaButton from '../../../../components/CtaButton';
import PlanPrice from '../../../../components/PlanPrice';
import PlanDetailsContainer from '../../../../components/PlanDetailsContainer';
import CheckMarkList from '../../../../components/CheckMarkList';
import PlanExampleContainer from '../../../../components/PlanExampleContainer';
import PlanDescription from '../../../../components/PlanDescription';
import PlanFeaturesList from '../../../../components/PlanFeaturesList';
import AreaCostCalculator from '../AreaCostCalculator';
import HighlightedPrice from '../../../../components/HighlightedPrice';
import TiersTable from '../TiersTable';
import { getAnnualPlanCheckoutLink } from '../../../../../shared/helpers/functions/navigation';
import { Plan } from '../../../../../shared/helpers/constants/plans';
import { StripeVolumePricingConfig } from '../../../../../shared/graphql/graphql';
import {
  getPluralAreaUnit,
  getShortAreaUnit,
} from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import {
  getCurrencyForAreaUnit,
  getCurrencySignForAreaUnit,
} from '../../../../../shared/helpers/functions/currency';
import useSubscriptionExample from '../../hooks/useSubscriptionExample';
import { MIN_ANNUAL_AREA_AMOUNTS } from '../../../../../shared/helpers/constants/areaAmounts';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import rootStore from '../../../rootStore/RootStore';
import PlanCalculatorTable from '../PlanCalculatorTable';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';

const AnnualPlan = observer(() => {
  const { t } = useTranslation();
  const { config, isLoaded: priceConfigLoaded } = usePriceConfig(rootStore);

  const { uiStateStore, areaUnitStore } = rootStore;
  const { annualPlan } = uiStateStore;
  const { areaUnit } = areaUnitStore;
  const currency = getCurrencyForAreaUnit(areaUnit);
  const currencySign = getCurrencySignForAreaUnit(areaUnit);
  const shortAreaUnit = getShortAreaUnit(areaUnit);
  const pluralAreaUnit = getPluralAreaUnit(areaUnit);

  const minAreaAmount = MIN_ANNUAL_AREA_AMOUNTS[areaUnit];
  const userSeatCost =
    config?.AnnualSubscription.AdditionalUserSeat.prices[currency].price;
  const planetImageryCost =
    config?.AnnualSubscription.PlanetImagery.prices[currency].price;
  const volumePricing = config?.AnnualSubscription
    .FullAccessToEssentialFunctionalities.prices[currency]
    .volumePricing as StripeVolumePricingConfig[];

  const { totalCost: exampleTotalCost } = useSubscriptionExample({
    subscription: {
      amount: minAreaAmount,
      volumePricing,
    },
  });

  const annualPlanCheckoutLink = getAnnualPlanCheckoutLink(
    areaUnit,
    annualPlan.area,
    annualPlan.additionalSeats,
    annualPlan.planetImagery,
  );

  const handleCtaClick = () => {
    AmplitudeAnalytics.trackPlanSelect(Plan.annual);
  };

  return (
    <PlanContainer highlighted showPlaceholder={!priceConfigLoaded}>
      <PlanDescription>
        <Fragment key="title">{t('plans.annual.title')}</Fragment>
        <Fragment key="description">
          {t('plans.annual.description', {
            areaUnit: pluralAreaUnit,
          })}
        </Fragment>
        <PlanPrice
          key="price"
          price={{
            period: t('plan-select.price-details.period.year'),
            value: `${currencySign}${exampleTotalCost}`,
          }}
        />
      </PlanDescription>
      <CtaButton
        label={t('plan-select.action-button.start-now')}
        to={annualPlanCheckoutLink}
        onClick={handleCtaClick}
      />
      <PlanDetailsContainer>
        <PlanFeaturesList
          title={t('plan-select.details.features')}
          items={[
            t('plans.annual.details.full-access-to-features'),
            <AreaCostCalculator />,
            t('plans.annual.details.one-user-seat-included'),
            t('plans.annual.details.minimum-order-size', {
              areaUnit: pluralAreaUnit,
              area: minAreaAmount,
            }),
          ]}
        />
        <PlanFeaturesList
          title={t('plan-select.details.optional-add-ons')}
          items={[
            <Trans
              i18nKey="plans.annual.details.additional-user-seat"
              values={{ price: userSeatCost, currency: currencySign }}
              components={{ 1: <HighlightedPrice renewable /> }}
            />,
            <Trans
              i18nKey="plans.details.planet-imagery"
              values={{
                price: planetImageryCost,
                currency: currencySign,
                areaUnit: shortAreaUnit,
              }}
              components={{ 1: <HighlightedPrice /> }}
            />,
            <Trans
              i18nKey="plans.annual.details.additional-volume"
              values={{
                areaUnit: pluralAreaUnit,
              }}
            />,
          ]}
        />
        <TiersTable />
      </PlanDetailsContainer>
      <PlanExampleContainer>
        <Fragment key="title">
          {t('plan-select.pricing-example.title')}
        </Fragment>
        <Fragment key="description">
          {t('plans.annual.pricing-calculator.description', {
            areaUnit: pluralAreaUnit,
          })}
        </Fragment>
        <PlanCalculatorTable key="content" />
      </PlanExampleContainer>
      <CheckMarkList
        title={t('plan-select.benefits')}
        items={[
          {
            title: t('plans.annual.benefits.comprehensive-access.title'),
            description: t(
              'plans.annual.benefits.comprehensive-access.description',
            ),
          },
          {
            title: t('plans.annual.benefits.predictable-costs.title'),
            description: t(
              'plans.annual.benefits.predictable-costs.description',
            ),
          },
          {
            title: t('plans.annual.benefits.volume-discounts.title'),
            description: t(
              'plans.annual.benefits.volume-discounts.description',
            ),
          },
          {
            title: t('plans.annual.benefits.flexibility.title'),
            description: t('plans.annual.benefits.flexibility.description', {
              areaUnit: pluralAreaUnit,
            }),
          },
        ]}
      />
    </PlanContainer>
  );
});

export default AnnualPlan;
